import * as THREE from 'three';
import { gsap } from 'gsap';
import { DAY_BACKGROUND_COLOR, NIGHT_BACKGROUND_COLOR } from './constants';

export default class LightingManager {
  constructor(scene) {
    this.scene = scene;
    this.setupLights();
    this.isDayMode = false;
  }

  setupLights() {
    // Create groups to hold the lights for easy mode switching
    this.dayLights = new THREE.Group();
    this.nightLights = new THREE.Group();
    this.scene.add(this.dayLights);
    this.scene.add(this.nightLights);
    
    // DAY MODE LIGHTS
    // Ambient light for day
    const dayAmbientLight = new THREE.AmbientLight(0xffffff, 0.7);
    this.dayLights.add(dayAmbientLight);
    
    // Main directional light (simulating sunlight)
    const dayMainLight = new THREE.DirectionalLight(0xffffff, 1.5);
    dayMainLight.position.set(2, 3, 2); // Adjusted position for closer wall
    dayMainLight.castShadow = true;
    dayMainLight.shadow.mapSize.width = 2048;
    dayMainLight.shadow.mapSize.height = 2048;
    this.dayLights.add(dayMainLight);
    
    // Additional lights for the desk scene
    const dayDeskLight = new THREE.PointLight(0xffffff, 1.2, 5); // Reduced distance
    dayDeskLight.position.set(0, 2, 0);
    dayDeskLight.castShadow = true;
    this.dayLights.add(dayDeskLight);
    
    // Soft fill light from the side
    const dayFillLight = new THREE.PointLight(0x5599ff, 0.5, 3); // Adjusted for closer space
    dayFillLight.position.set(-1, 2, 0);
    this.dayLights.add(dayFillLight);
    
    // Add a subtle accent light for the side of the desk
    const dayAccentLight = new THREE.PointLight(0xffaa55, 0.3, 4);
    dayAccentLight.position.set(1.5, 1, 1);
    this.dayLights.add(dayAccentLight);
    
    // NIGHT MODE LIGHTS
    // Ambient light for night (dimmer, cooler)
    const nightAmbientLight = new THREE.AmbientLight(0x334455, 0.3);
    this.nightLights.add(nightAmbientLight);
    
    // Monitor light (screen glow) - main light source at night
    const monitorLight = new THREE.PointLight(0x6688cc, 1.5, 2);
    monitorLight.position.set(0, 1.3, -0.2);
    this.nightLights.add(monitorLight);
    
    // Desk lamp light (warm light)
    const deskLampLight = new THREE.SpotLight(0xff8844, 1.2, 3, Math.PI/4, 0.5, 1);
    deskLampLight.position.set(-0.7, 1.5, 0.2);
    deskLampLight.target.position.set(-0.7, 0.8, 0.2);
    deskLampLight.castShadow = true;
    this.nightLights.add(deskLampLight);
    this.nightLights.add(deskLampLight.target);
    
    // Wall accent light (soft blue glow)
    const wallLight = new THREE.PointLight(0x8888ff, 0.4, 2);
    wallLight.position.set(0, 2, -0.4);
    this.nightLights.add(wallLight);
    
    // Start in night mode
    this.toggleDayNightMode(false);
  }

  toggleDayNightMode(isDayMode = !this.isDayMode) {
    this.isDayMode = isDayMode;
    
    // Toggle lights
    this.dayLights.visible = isDayMode;
    this.nightLights.visible = !isDayMode;
    
    // Change scene background
    if (this.scene.background) {
      gsap.to(this.scene.background, {
        r: isDayMode ? DAY_BACKGROUND_COLOR.r : NIGHT_BACKGROUND_COLOR.r,
        g: isDayMode ? DAY_BACKGROUND_COLOR.g : NIGHT_BACKGROUND_COLOR.g,
        b: isDayMode ? DAY_BACKGROUND_COLOR.b : NIGHT_BACKGROUND_COLOR.b,
        duration: 1
      });
    }
  }

  updateScreenBrightness(screen) {
    if (screen && screen.material) {
      const screenBrightness = this.isDayMode ? 0 : 0.2;
      gsap.to(screen.material.color, {
        r: screenBrightness,
        g: screenBrightness,
        b: screenBrightness,
        duration: 1
      });
    }
  }
} 